import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Upload,
  Button,
  message,
  Divider,
  Switch,
  Spin,
} from "antd";
import { UploadOutlined, InboxOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { useAuth } from "../../contexts/AuthContext";
import NotificationSettings from "./NotificationSettings";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const { TextArea } = Input;
const { Dragger } = Upload;

const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 8px;
  }
`;

const FormSection = styled.div`
  margin-bottom: 20px;
`;

const SectionTitle = styled.h5`
  font-weight: 600;
  color: #3c2f61;
  margin-bottom: 10px;
`;

const UploadArea = styled.div`
  .ant-upload.ant-upload-drag {
    border: 2px dashed #d9d9d9;
    border-radius: 6px;
    background-color: #fafafa;
    
    &:hover {
      border-color: #3c2f61;
    }
  }
`;

const CreateEventModal = ({ visible, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [notifyMembers, setNotifyMembers] = useState(true);
  const [selectedMemberEmails, setSelectedMemberEmails] = useState([]);
  const [additionalEmails, setAdditionalEmails] = useState([]);

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("name", values.name);
      formData.append("event_date", values.event_date.format("YYYY-MM-DD"));
      formData.append("amount", values.amount);
      formData.append("notify_members", notifyMembers);

      if (values.description) {
        formData.append("description", values.description);
      }

      if (values.physical_location) {
        formData.append("physical_location", values.physical_location);
      }

      if (values.payment_info) {
        formData.append("payment_info", values.payment_info);
      }

      if (imageFile) {
        formData.append("image", imageFile);
      }

      if (notifyMembers) {
        if (selectedMemberEmails.length > 0) {
          formData.append("member_emails", JSON.stringify(selectedMemberEmails));
        }
        if (additionalEmails.length > 0) {
          formData.append("additional_emails", JSON.stringify(additionalEmails));
        }
      }

      const response = await axios.post(
        `${BASE_URL}/companies/events/create`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.status === 200 && response.data.status === "success") {
        message.success("Event created successfully!");
        form.resetFields();
        setImageFile(null);
        setNotifyMembers(false);
        setSelectedMemberEmails([]);
        setAdditionalEmails([]);
        onSuccess();
      } else {
        message.error(response.data.message || "Failed to create event.");
      }
    } catch (error) {
      console.error("Error creating event:", error);
      if (error.response?.data?.detail) {
        message.error(error.response.data.detail);
      } else {
        message.error("Failed to create event. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (info) => {
    if (info.file.status === "removed") {
      setImageFile(null);
      return;
    }

    const file = info.file.originFileObj || info.file;
    const isImage = file.type.startsWith("image/");
    const isValidFormat = ["image/jpeg", "image/png", "image/webp"].includes(file.type);
    const isLt5M = file.size / 1024 / 1024 < 5;

    if (!isImage || !isValidFormat) {
      message.error("You can only upload JPG/PNG/WEBP files!");
      return;
    }

    if (!isLt5M) {
      message.error("Image must be smaller than 5MB!");
      return;
    }

    setImageFile(file);
  };

  const uploadProps = {
    accept: "image/jpeg,image/png,image/webp",
    multiple: false,
    maxCount: 1,
    beforeUpload: () => false,
    onChange: handleFileChange,
    onRemove: () => {
      setImageFile(null);
    },
  };

  const handleCancel = () => {
    form.resetFields();
    setImageFile(null);
    setNotifyMembers(false);
    setSelectedMemberEmails([]);
    setAdditionalEmails([]);
    onCancel();
  };

  return (
    <StyledModal
      title="Create New Event"
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={700}
      destroyOnClose
    >
      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <FormSection>
            <SectionTitle>Event Details</SectionTitle>
            
            <Form.Item
              name="name"
              label="Event Name"
              rules={[{ required: true, message: "Please enter event name" }]}
            >
              <Input placeholder="Enter event name" />
            </Form.Item>

            <Form.Item
              name="description"
              label="Description"
            >
              <TextArea
                rows={4}
                placeholder="Enter event description"
              />
            </Form.Item>

            <Form.Item
              name="event_date"
              label="Event Date"
              rules={[{ required: true, message: "Please select event date" }]}
            >
              <DatePicker
                style={{ width: "100%" }}
                format="YYYY-MM-DD"
              />
            </Form.Item>

            <Form.Item
              name="physical_location"
              label="Physical Location"
            >
              <Input placeholder="Enter event location" />
            </Form.Item>
          </FormSection>

          <FormSection>
            <SectionTitle>Pricing & Payment</SectionTitle>
            
            <Form.Item
              name="amount"
              label="Amount (per person)"
              rules={[
                { required: true, message: "Please enter amount" },
                { type: "number", min: 0, message: "Amount must be positive" }
              ]}
            >
              <InputNumber
                style={{ width: "100%" }}
                placeholder="Enter amount"
                formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={value => value.replace(/\$\s?|(,*)/g, '')}
              />
            </Form.Item>

            <Form.Item
              name="payment_info"
              label="Payment Information"
            >
              <TextArea
                rows={3}
                placeholder="Enter payment instructions or details"
              />
            </Form.Item>
          </FormSection>

          <FormSection>
            <SectionTitle>Event Image</SectionTitle>
            
            <UploadArea>
              <Dragger {...uploadProps}>
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                  Click or drag image to this area to upload
                </p>
                <p className="ant-upload-hint">
                  Support for JPG, PNG, or WEBP. Maximum size: 5MB
                </p>
              </Dragger>
            </UploadArea>
          </FormSection>

          <Divider />

          <FormSection>
            <div style={{ display: "flex", alignItems: "center", marginBottom: "15px" }}>
              <SectionTitle style={{ marginBottom: 0, marginRight: "10px" }}>
                Notify Members Via Email
              </SectionTitle>
              <Switch
                checked={notifyMembers}
                onChange={setNotifyMembers}
                checkedChildren="ON"
                unCheckedChildren="OFF"
              />
            </div>

            {notifyMembers && (
              <NotificationSettings
                selectedMemberEmails={selectedMemberEmails}
                setSelectedMemberEmails={setSelectedMemberEmails}
                additionalEmails={additionalEmails}
                setAdditionalEmails={setAdditionalEmails}
              />
            )}
          </FormSection>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "20px" }}>
            <Button onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              style={{ backgroundColor: "#3c2f61", borderColor: "#3c2f61" }}
            >
              Create Event
            </Button>
          </div>
        </Form>
      </Spin>
    </StyledModal>
  );
};

export default CreateEventModal;